import React, { useState } from "react";
import { ArrowRight, Check } from "lucide-react";

const INTEGRATIONS = {
  ESPs: [
    { name: "Mailchimp", logo: "MC", color: "amber" },
    { name: "SendGrid", logo: "SG", color: "blue" },
    { name: "Klaviyo", logo: "KL", color: "emerald" },
    { name: "ActiveCampaign", logo: "AC", color: "violet" },
  ],
  CRMs: [
    { name: "HubSpot", logo: "HS", color: "orange" },
    { name: "Salesforce", logo: "SF", color: "sky" },
    { name: "Pipedrive", logo: "PD", color: "emerald" },
    { name: "Zoho CRM", logo: "ZC", color: "red" },
  ],
  Forms: [
    { name: "Typeform", logo: "TF", color: "slate" },
    { name: "Google Forms", logo: "GF", color: "blue" },
    { name: "Jotform", logo: "JF", color: "orange" },
    { name: "Gravity Forms", logo: "GV", color: "violet" },
  ],
  CMS: [
    { name: "WordPress", logo: "WP", color: "blue" },
    { name: "Webflow", logo: "WF", color: "indigo" },
    { name: "Shopify", logo: "SP", color: "emerald" },
    { name: "Wix", logo: "WX", color: "slate" },
  ],
  Automation: [
    { name: "Zapier", logo: "ZP", color: "orange" },
    { name: "Make", logo: "MK", color: "violet" },
    { name: "n8n", logo: "N8", color: "red" },
    { name: "Integromat", logo: "IG", color: "blue" },
  ],
};

const colorClasses = {
  amber: "from-amber-500 to-orange-600",
  blue: "from-blue-500 to-indigo-600",
  emerald: "from-emerald-500 to-teal-600",
  violet: "from-violet-500 to-purple-600",
  orange: "from-orange-500 to-red-600",
  sky: "from-sky-500 to-blue-600",
  red: "from-red-500 to-rose-600",
  slate: "from-slate-500 to-gray-600",
  indigo: "from-indigo-500 to-purple-600",
};

export default function IntegrationsSection() {
  const [activeCategory, setActiveCategory] = useState("ESPs");

  return (
    <section className="py-16 lg:py-24 bg-slate-50 dark:bg-slate-900/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-slate-50 mb-4">
            Works with your{" "}
            <span className="text-sky-600 dark:text-sky-400">
              existing tools
            </span>
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
            Connect VALIDMAIL to the tools you already use in minutes. No
            complex setup, no developer required.
          </p>
        </div>

        <div className="mb-12">
          <div className="flex flex-wrap justify-center gap-3">
            {Object.keys(INTEGRATIONS).map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-full font-semibold transition-all ${
                  activeCategory === category
                    ? "bg-sky-600 text-white shadow-lg scale-105"
                    : "bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-2 border-slate-200 dark:border-slate-700 hover:border-sky-300 dark:hover:border-sky-700"
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {INTEGRATIONS[activeCategory].map((integration, index) => (
            <div
              key={index}
              className="group bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800 hover:border-sky-300 dark:hover:border-sky-700 transition-all hover:shadow-xl hover:-translate-y-2 cursor-pointer"
            >
              <div className="flex flex-col items-center text-center">
                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${
                    colorClasses[integration.color]
                  } flex items-center justify-center text-white font-bold text-xl mb-4 shadow-lg group-hover:scale-110 transition-transform`}
                >
                  {integration.logo}
                </div>

                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-50 mb-2">
                  {integration.name}
                </h3>

                <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 mb-4">
                  <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                  <span>Ready to connect</span>
                </div>

                <button className="text-sm font-semibold text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 flex items-center gap-1 group-hover:gap-2 transition-all">
                  Connect now
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-gradient-to-r from-sky-50 via-blue-50 to-indigo-50 dark:from-sky-900/20 dark:via-blue-900/20 dark:to-indigo-900/20 rounded-3xl p-8 md:p-12 border-2 border-sky-200 dark:border-sky-800">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-slate-900 dark:text-slate-50 mb-4">
                Don't see your tool?
              </h3>
              <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                Use our powerful REST API or Zapier integration to connect with
                any platform. Our developer docs make integration a breeze.
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-sky-600 dark:text-sky-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300">
                    RESTful API with comprehensive documentation
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-sky-600 dark:text-sky-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300">
                    SDKs for Node.js, Python, PHP, Ruby, and more
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-sky-600 dark:text-sky-400 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700 dark:text-slate-300">
                    Webhooks for real-time updates
                  </span>
                </li>
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800">
                <div className="text-3xl font-bold text-sky-600 dark:text-sky-400 mb-2">
                  50+
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Pre-built integrations
                </div>
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800">
                <div className="text-3xl font-bold text-sky-600 dark:text-sky-400 mb-2">
                  &lt; 5min
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Average setup time
                </div>
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800">
                <div className="text-3xl font-bold text-sky-600 dark:text-sky-400 mb-2">
                  24/7
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Integration support
                </div>
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 border-2 border-slate-200 dark:border-slate-800">
                <div className="text-3xl font-bold text-sky-600 dark:text-sky-400 mb-2">
                  100%
                </div>
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Uptime SLA
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* <div className="mt-12 text-center">
          <button className="inline-flex items-center gap-2 px-8 py-4 rounded-full text-base font-semibold border-2 border-sky-600 text-sky-600 hover:bg-sky-600 hover:text-white dark:border-sky-500 dark:text-sky-400 dark:hover:bg-sky-500 dark:hover:text-white transition-all duration-300 shadow-md hover:shadow-xl">
            View all integrations
            <ArrowRight className="h-5 w-5" />
          </button>
        </div> */}
      </div>
    </section>
  );
}
